export interface MoveInput {
  x: number;
  y: number;
}

const DEAD_ZONE = 0.12;
const STICK_RADIUS = 50;

// Slide-swipe gesture tuning — mirrors Classic's own "rolling anchor"
// technique (blast-runner skill gotcha #40): a swipe detector armed only at
// pointerdown is unusable on a zone the thumb holds continuously to move,
// so the anchor re-arms on upward movement or staleness instead of firing
// once and going dead.
const SLIDE_TRIGGER_PX = 42;
const SLIDE_REARM_UP_PX = 10;
const SLIDE_STALE_MS = 260;

/**
 * Left-half-of-screen virtual stick, driven by Pointer Events so mouse (desktop
 * testing) and touch (phone) share one code path. Right half is reserved for
 * S01's orbit camera / dev-only teleport raycast — never claimed here. The
 * boundary column (clientX exactly at the half-width) belongs to the right
 * side: this guard uses `>=`, OrbitLook's guard uses `<`, so exactly one of
 * the two ever engages on a boundary tap.
 *
 * Also owns the S01 slide/dodge gesture: a swipe-down while the stick is
 * held, using the RAW pointer position (not the clamped knob) for precision.
 */
class VirtualStick {
  x = 0;
  y = 0;
  private active = false;
  private pointerId: number | null = null;
  private originX = 0;
  private originY = 0;
  private readonly zone: HTMLElement;
  private readonly baseEl: HTMLDivElement;
  private readonly knobEl: HTMLDivElement;
  private readonly onSlideDown: () => void;

  private slideAnchorY = 0;
  private slideAnchorAt = 0;
  private readonly isLefty: () => boolean;

  constructor(zone: HTMLElement, onSlideDown: () => void, isLefty: () => boolean) {
    this.zone = zone;
    this.onSlideDown = onSlideDown;
    this.isLefty = isLefty;
    this.baseEl = document.createElement('div');
    this.baseEl.className = 'br3d-stick-base';
    this.baseEl.style.display = 'none';
    this.knobEl = document.createElement('div');
    this.knobEl.className = 'br3d-stick-knob';
    this.knobEl.style.display = 'none';
    document.body.append(this.baseEl, this.knobEl);

    zone.addEventListener('pointerdown', this.onPointerDown);
    window.addEventListener('pointermove', this.onPointerMove);
    window.addEventListener('pointerup', this.onPointerUp);
    window.addEventListener('pointercancel', this.onPointerUp);
  }

  get isActive(): boolean {
    return this.active;
  }

  /**
   * Force-clear the stick regardless of whether a real pointerup/pointercancel
   * ever arrives. Without this, a mouse released outside the window, or an
   * iOS Safari tab-switch mid-touch (which can freeze the page without firing
   * either event), latches `active=true` forever — the player then drifts
   * indefinitely AND the keyboard fallback stays dead too, since
   * InputSystem.update() defers to the stick whenever it's active. Only a
   * page reload recovered before this existed.
   */
  reset(): void {
    if (!this.active) return;
    this.active = false;
    this.pointerId = null;
    this.x = 0;
    this.y = 0;
    this.baseEl.style.display = 'none';
    this.knobEl.style.display = 'none';
  }

  private onPointerDown = (e: PointerEvent): void => {
    if (this.active) return;
    // Normally owns the LEFT half (camera/dev tools own the right); S07's
    // lefty mode mirrors this — the move stick moves to whichever half
    // OrbitLook does NOT own, so the two remain complementary (exactly one
    // owns the boundary column) at either setting, same invariant the
    // original `>=`/`<` split already guaranteed.
    const half = window.innerWidth / 2;
    const onOwnedSide = this.isLefty() ? e.clientX >= half : e.clientX < half;
    if (!onOwnedSide) return;
    this.active = true;
    this.pointerId = e.pointerId;
    this.originX = e.clientX;
    this.originY = e.clientY;
    this.x = 0;
    this.y = 0;
    this.slideAnchorY = e.clientY;
    this.slideAnchorAt = performance.now();
    this.zone.setPointerCapture(e.pointerId);
    this.showAt(this.originX, this.originY);
  };

  private onPointerMove = (e: PointerEvent): void => {
    if (!this.active || e.pointerId !== this.pointerId) return;
    const dx = e.clientX - this.originX;
    const dy = e.clientY - this.originY;
    const dist = Math.hypot(dx, dy);
    const clamped = Math.min(dist, STICK_RADIUS);
    const angle = Math.atan2(dy, dx);
    this.knobEl.style.left = `${this.originX + Math.cos(angle) * clamped}px`;
    this.knobEl.style.top = `${this.originY + Math.sin(angle) * clamped}px`;

    const n = clamped / STICK_RADIUS;
    const eased = n < DEAD_ZONE ? 0 : (n - DEAD_ZONE) / (1 - DEAD_ZONE);
    this.x = Math.cos(angle) * eased;
    this.y = Math.sin(angle) * eased;

    this.updateSlideGesture(e.clientY);
  };

  private updateSlideGesture(clientY: number): void {
    const now = performance.now();
    const upward = clientY - this.slideAnchorY < -SLIDE_REARM_UP_PX;
    const stale = now - this.slideAnchorAt > SLIDE_STALE_MS;
    if (upward || stale) {
      this.slideAnchorY = clientY;
      this.slideAnchorAt = now;
      return;
    }
    if (clientY - this.slideAnchorY > SLIDE_TRIGGER_PX) {
      this.onSlideDown();
      // Re-anchor immediately rather than latching "fired" — the
      // controller's own slide state (can't re-slide mid-slide) is the
      // repeat gate, per the same gotcha's "let the ability's cooldown
      // gate repeats, not a fired-latch" lesson.
      this.slideAnchorY = clientY;
      this.slideAnchorAt = now;
    }
  }

  private onPointerUp = (e: PointerEvent): void => {
    if (!this.active || e.pointerId !== this.pointerId) return;
    this.reset();
  };

  private showAt(cx: number, cy: number): void {
    this.baseEl.style.left = `${cx}px`;
    this.baseEl.style.top = `${cy}px`;
    this.baseEl.style.display = 'block';
    this.knobEl.style.left = `${cx}px`;
    this.knobEl.style.top = `${cy}px`;
    this.knobEl.style.display = 'block';
  }
}

/**
 * Right-half drag → camera orbit deltas. Same unified Pointer Events /
 * force-reset-on-blur discipline as `VirtualStick` (gotcha lesson carried
 * over verbatim: an unreset latch here would freeze the camera-relative
 * movement basis, not just drift the player).
 */
class OrbitLook {
  private active = false;
  private pointerId: number | null = null;
  private lastX = 0;
  private lastY = 0;
  private accumDx = 0;
  private accumDy = 0;
  private readonly isLefty: () => boolean;

  constructor(zone: HTMLElement, isLefty: () => boolean) {
    this.isLefty = isLefty;
    zone.addEventListener('pointerdown', this.onPointerDown);
    window.addEventListener('pointermove', this.onPointerMove);
    window.addEventListener('pointerup', this.onPointerUp);
    window.addEventListener('pointercancel', this.onPointerUp);
    window.addEventListener('blur', this.reset);
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.reset();
    });
  }

  reset = (): void => {
    this.active = false;
    this.pointerId = null;
  };

  private onPointerDown = (e: PointerEvent): void => {
    if (this.active) return;
    // Always the complement of VirtualStick's own owned side (see its
    // onPointerDown comment) — right half normally, left half in lefty mode.
    const half = window.innerWidth / 2;
    const onOwnedSide = this.isLefty() ? e.clientX < half : e.clientX >= half;
    if (!onOwnedSide) return;
    this.active = true;
    this.pointerId = e.pointerId;
    this.lastX = e.clientX;
    this.lastY = e.clientY;
  };

  private onPointerMove = (e: PointerEvent): void => {
    if (!this.active || e.pointerId !== this.pointerId) return;
    this.accumDx += e.clientX - this.lastX;
    this.accumDy += e.clientY - this.lastY;
    this.lastX = e.clientX;
    this.lastY = e.clientY;
  };

  private onPointerUp = (e: PointerEvent): void => {
    if (!this.active || e.pointerId !== this.pointerId) return;
    this.reset();
  };

  /** Drains and returns the accumulated drag delta since the last call. */
  consume(): { dx: number; dy: number } {
    const d = { dx: this.accumDx, dy: this.accumDy };
    this.accumDx = 0;
    this.accumDy = 0;
    return d;
  }
}

/**
 * Fixed on-screen jump button — a real DOM element (not part of either
 * half's drag zone), so it needs no boundary convention: it's hit-tested by
 * the browser like any button, sitting visually on top of the canvas via
 * z-index. `stopPropagation` is defensive (the button isn't currently a DOM
 * descendant of either drag zone, so bubbling wouldn't reach them anyway,
 * but keeps this safe if the DOM structure ever changes).
 */
class JumpButton {
  readonly el: HTMLButtonElement;
  private pressed = false;

  constructor() {
    this.el = document.createElement('button');
    this.el.className = 'br3d-jump-btn';
    this.el.textContent = 'JUMP';
    this.el.addEventListener('pointerdown', (e) => {
      e.stopPropagation();
      e.preventDefault();
      this.pressed = true;
    });
    document.body.append(this.el);
  }

  /** Edge-triggered: true at most once per physical press. */
  consume(): boolean {
    if (!this.pressed) return false;
    this.pressed = false;
    return true;
  }
}

/**
 * Unified move input: the virtual stick while it's actively held, otherwise a
 * WASD/arrow-key fallback (desktop verification convenience). Both agree on
 * sign convention: y negative = forward. Also bundles S01's look (right-half
 * drag, mouse-drag fallback via the same Pointer Events path), jump button,
 * and slide gesture/key.
 */
export class InputSystem {
  readonly move: MoveInput = { x: 0, y: 0 };
  private readonly stick: VirtualStick;
  private readonly look: OrbitLook;
  private readonly jumpBtn: JumpButton;
  private readonly keys = new Set<string>();
  private slideKeyArmed = true;
  private requestJumpFromKeyboard = false;
  /** S07: which half of the screen the move stick vs. camera-look own — see VirtualStick/OrbitLook's own onPointerDown comments for the complementary-side invariant this preserves either way. */
  private lefty = false;

  onSlide: (() => void) | null = null;

  constructor(zone: HTMLElement) {
    this.stick = new VirtualStick(zone, () => this.onSlide?.(), () => this.lefty);
    this.look = new OrbitLook(zone, () => this.lefty);
    this.jumpBtn = new JumpButton();
    window.addEventListener('keydown', (e) => {
      this.keys.add(e.code);
      if (e.code === 'ShiftLeft' || e.code === 'ShiftRight') {
        if (this.slideKeyArmed) {
          this.slideKeyArmed = false;
          this.onSlide?.();
        }
      }
    });
    window.addEventListener('keyup', (e) => {
      this.keys.delete(e.code);
      if (e.code === 'ShiftLeft' || e.code === 'ShiftRight') this.slideKeyArmed = true;
    });
    window.addEventListener('blur', this.resetAll);
    // Same reset as blur, not a narrower one — `visibilitychange` is the
    // MORE reliable signal for backgrounding (guaranteed on tab-switch/
    // minimize/mobile-app-backgrounding; blur is comparatively narrower),
    // so a handler here that skipped `keys`/`slideKeyArmed` left a Shift
    // held at the moment of backgrounding stuck "armed=false" until a
    // stray keyup happened to arrive, silently deafening the slide gesture
    // on return — confirmed by a 3-lens review. Both handlers now share
    // one method so they can't drift apart again.
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.resetAll();
    });
  }

  private resetAll = (): void => {
    this.keys.clear();
    this.stick.reset();
    this.look.reset();
    this.slideKeyArmed = true;
  };

  /** S07: force-clears all live input state — the same recovery path `blur`/`visibilitychange` already use, exposed for the pause menu. Opening pause must not leave a stale drag/stick active underneath the overlay (a `setPointerCapture`'d drag keeps receiving window-level move/up events regardless of what's visually on top). */
  forceReset(): void {
    this.resetAll();
  }

  /** S07: swaps which half of the screen owns the move stick vs. camera-look, and (via main.ts's own `body.lefty` CSS class, applied alongside this call) mirrors the fixed button cluster. Safe to call mid-drag — an ALREADY-active stick/look keeps running on its captured pointer until release; only the next fresh pointerdown honors the new side. */
  setLefty(v: boolean): void {
    this.lefty = v;
  }

  update(): void {
    if (this.stick.isActive) {
      this.move.x = this.stick.x;
      this.move.y = this.stick.y;
    } else {
      let x = 0;
      let y = 0;
      if (this.keys.has('KeyA') || this.keys.has('ArrowLeft')) x -= 1;
      if (this.keys.has('KeyD') || this.keys.has('ArrowRight')) x += 1;
      if (this.keys.has('KeyW') || this.keys.has('ArrowUp')) y -= 1;
      if (this.keys.has('KeyS') || this.keys.has('ArrowDown')) y += 1;
      const len = Math.hypot(x, y);
      this.move.x = len > 0 ? x / len : 0;
      this.move.y = len > 0 ? y / len : 0;
    }
    if (this.keys.has('Space')) {
      this.keys.delete('Space'); // edge-trigger the keyboard jump the same way the button consumes a press
      this.requestJumpFromKeyboard = true;
    }
  }

  /** Edge-triggered jump request: the on-screen button OR the keyboard fallback (Space). */
  consumeJump(): boolean {
    const fromButton = this.jumpBtn.consume();
    const fromKeyboard = this.requestJumpFromKeyboard;
    this.requestJumpFromKeyboard = false;
    return fromButton || fromKeyboard;
  }

  /** Drains the accumulated look-drag delta since the last call. */
  consumeLook(): { dx: number; dy: number } {
    return this.look.consume();
  }
}
